import { ReactElement, Suspense, useCallback, useEffect, useRef, useState } from "react";
import DocumentMeta from "react-document-meta";
import { useRouterContext, RouterWithModals } from "@rotorstudios/fe-modules";
import { GlobalStyles } from "@tmca/toyota-kit";

import { CMS_TEMPLATES_GR } from "../_common/modules/Data/static";
import useGAPageView from "../_common/modules/fe-modules/GA/useGAPageView";
import useAppLoadingSequence from "../_common/modules/Data/useAppLoadingSequence";
import useUpdateUserData from "../_common/modules/Data/useUpdateUserData";
import usePrevious from "../_common/hooks/usePrevious";
import UiMModalOverlay from "../_common/uiModules/uiMModalOverlay/UiMModalOverlay";
import UiMLoading from "../_common/uiModules/uiMLoading/UiMLoading";
import { compareMaps } from "../_common/helpers/utils";
import { useThemeContext } from "../_common/theme/ThemeProvider";
import { useDataContext } from "../_common/modules/Data/DataProvider";
import { useCmeContext } from "../_common/modules/fe-cme/CMEProvider";
import { PAGE_ERROR } from "../_common/modules/fe-modules/Router/static";
import { ROUTES_PAGES_MAIN_TREE } from "../_common/modules/fe-modules/Router/routesPagesTree";
import "./_Index.scss";

const Index = (): ReactElement => {
  useGAPageView();
  useAppLoadingSequence();

  const {
    cmeSiloUiData,
    vehicleData,
    errorState,
    updateIsLoading,
    updateMaterialCode,
    updateVehicleEngine,
  } = useDataContext();
  const { gotoPage } = useRouterContext();
  const { cmeOptions, setInitialUserConfig, getMaterialCode, getEngine } =
    useCmeContext();
  const { updateVehicleConfigData } = useUpdateUserData();
  const prevSelectedOptions = usePrevious(cmeOptions.selectedOptions);

  const { theme, setTheme } = useThemeContext();

  const [meta, setMeta] = useState<any>(null);
  const [classNames, setClassNames] = useState<string[]>(["Index"]);
  const [disableUpdateVehicle, setDisableUpdateVehicle] = useState<boolean>(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleSetMatCodeAndEngine = useCallback(() => {
    const materialCode = getMaterialCode();
    const engine = getEngine();
    updateMaterialCode(materialCode);
    updateVehicleEngine(engine);
  }, [getMaterialCode, getEngine, updateMaterialCode, updateVehicleEngine]);

  const handleInitialVehicleLoad = useCallback(() => {
    if (vehicleData?.initConfig) {
      setInitialUserConfig(vehicleData.initConfig);
      setDisableUpdateVehicle(true);
    }
    handleSetMatCodeAndEngine();
    window?.scrollTo(0, 0);
    setTimeout(() => {
      updateIsLoading(false);
    }, 1000);
  }, [vehicleData, setInitialUserConfig, updateIsLoading, handleSetMatCodeAndEngine]);

  const handleUpdateVehicleConfig = useCallback(() => {
    // Prevents unneccessary update of pin, url, cookie data
    // after initial user configuration applied to CME
    if (disableUpdateVehicle) {
      setDisableUpdateVehicle(false);
    } else {
      updateVehicleConfigData();
    }
    handleSetMatCodeAndEngine();
  }, [disableUpdateVehicle, updateVehicleConfigData, handleSetMatCodeAndEngine]);

  // *************
  // Error
  // *************
  useEffect(() => {
    if (errorState) {
      gotoPage(PAGE_ERROR);
    }
  }, [errorState, gotoPage]);

  // *************
  // CME initialised
  // *************
  useEffect(() => {
    if (!prevSelectedOptions && cmeOptions.selectedOptions) {
      handleInitialVehicleLoad();
    }
  }, [prevSelectedOptions, cmeOptions.selectedOptions, handleInitialVehicleLoad]);

  // *************
  // CME configuration updated
  // *************
  useEffect(() => {
    if (
      prevSelectedOptions &&
      cmeOptions.selectedOptions &&
      !compareMaps(prevSelectedOptions, cmeOptions.selectedOptions)
    ) {
      handleUpdateVehicleConfig();
    }
  }, [prevSelectedOptions, cmeOptions.selectedOptions, handleUpdateVehicleConfig]);

  useEffect(() => {
    const template = cmeSiloUiData?.model?.template;

    if (!meta) {
      setMeta({
        meta: {
          template,
        },
      });
    }

    const titleClass = cmeSiloUiData?.model?.title?.replaceAll(/\s+/g, "-").toLowerCase();
    const templateClass = cmeSiloUiData?.model?.template
      ?.replaceAll(/\s+/g, "-")
      .toLowerCase();

    const newClassNames = ["Index"];
    if (titleClass) newClassNames.push(titleClass);
    if (templateClass) newClassNames.push(templateClass);

    setClassNames(newClassNames);
  }, [meta, cmeSiloUiData]);

  useEffect(() => {
    if (ref.current?.classList.contains(CMS_TEMPLATES_GR)) {
      setTheme("dark");
    } else {
      setTheme("light");
    }
  }, [ref.current?.classList.value, setTheme]);

  return (
    <DocumentMeta {...meta}>
      <div ref={ref} className={classNames.join(" ")}>
        <GlobalStyles theme={theme === "light" ? "charcoalOnWhite" : "whiteOnCharcoal"} />
        <UiMLoading />
        <Suspense fallback={<></>}>
          <UiMModalOverlay />
        </Suspense>
        <RouterWithModals pages={ROUTES_PAGES_MAIN_TREE} modals={[]} />
      </div>
    </DocumentMeta>
  );
};

export default Index;
